/* rbac/RbacContext — derives effective role, org/workspace IDs, and backend
   capabilities from the auth session (via nx-session-changed events from AuthProvider).
   Falls back to the last-known state for instant hydration on mount. */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ROLES, roleFromSession, can as canByRole } from './roles';

const RbacCtx = createContext(null);

const sessionToRbac = (user) => {
  if (!user) return null;
  return {
    role:           roleFromSession(user.effectiveRole || user.platformRole),
    orgId:          user.activeOrgId        || null,
    workspaceId:    user.activeWorkspaceId  || null,
    capabilities:   Array.isArray(user.capabilities)   ? user.capabilities   : [],
    enabledModules: Array.isArray(user.enabledModules) ? user.enabledModules : [],
  };
};

const loadInitial = () => {
  /* Prefer live session from AuthProvider if already set */
  const live = sessionToRbac(window.__nx_auth?.user);
  if (live && live.orgId) return live;

  /* Fall back to last-known state for instant hydration (avoids flash) */
  try {
    const cached = JSON.parse(localStorage.getItem('nx-session-v1'));
    /* RG-004: only trust cached session if it has a valid expiresAt (written by cacheSession) */
    if (cached?.expiresAt && Date.now() <= cached.expiresAt) {
      return {
        role:           roleFromSession(cached.effectiveRole || cached.platformRole),
        orgId:          cached.activeOrgId        || null,
        workspaceId:    cached.activeWorkspaceId  || null,
        capabilities:   Array.isArray(cached.capabilities)   ? cached.capabilities   : [],
        enabledModules: Array.isArray(cached.enabledModules) ? cached.enabledModules : [],
      };
    }
  } catch { /* ignore */ }

  return { role: ROLES.WS_USER, orgId: null, workspaceId: null, capabilities: [], enabledModules: [] };
};

export const RbacProvider = ({ children }) => {
  const [rbac, setRbac] = useState(loadInitial);

  /* Sync with session-changed events from AuthProvider */
  useEffect(() => {
    const onSessionChanged = (e) => {
      const user = e.detail?.user;
      if (!user) {
        setRbac({ role: ROLES.WS_USER, orgId: null, workspaceId: null, capabilities: [], enabledModules: [] });
        return;
      }
      const next = sessionToRbac(user);
      if (next) setRbac(next);
    };

    window.addEventListener('nx-session-changed', onSessionChanged);
    return () => window.removeEventListener('nx-session-changed', onSessionChanged);
  }, []);

  /* setOrg/setWorkspace: optimistic updates used by WorkspaceSwitcher while
     awaiting the nx-session-changed event from auth.switchWorkspace(). */
  const setOrg       = useCallback((orgId)       => setRbac(s => ({ ...s, orgId })),       []);
  const setWorkspace = useCallback((workspaceId) => setRbac(s => ({ ...s, workspaceId })), []);

  const value = useMemo(() => ({
    ...rbac,
    setOrg, setWorkspace,
    /* Primary: check backend capabilities[] (authoritative source from server session).
       Fallback: role-rank check for unauthenticated or empty-capability states. */
    can: (action) => {
      if (rbac.capabilities.length > 0) return rbac.capabilities.includes(action);
      return canByRole(rbac.role, action);
    },
  }), [rbac, setOrg, setWorkspace]);

  /* SEC-004: expose only read-only session data on window; setter functions must
     not be globally accessible — they allow client-side privilege escalation */
  globalThis.__nx_rbac = { role: rbac.role, orgId: rbac.orgId, workspaceId: rbac.workspaceId, enabledModules: rbac.enabledModules, can: value.can };

  return <RbacCtx.Provider value={value}>{children}</RbacCtx.Provider>;
};

export const useRbac = () => {
  const ctx = useContext(RbacCtx);
  if (!ctx) throw new Error('useRbac must be used inside RbacProvider');
  return ctx;
};
